import type { Frequency } from '@generated/graphql';
import { DateField } from '@ovComponents/2-component/dateField/dateField';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import { Radio, RadioGroup } from '@ovComponents/2-component/radio/radio';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { MULTI_PORTFOLIO_DEPOSIT_TEST_ID } from '@ovComponents/4-module/workflowCompletion/constants/testIds';
import {
  DepositStarting,
  resolveScheduledDateMinDate,
  resolveScheduleFieldsVisibility,
  SCHEDULE_FREQUENCIES,
} from '@ovComponents/4-module/workflowCompletion/subSteps/utils/depositSchedule';
import type { Dayjs } from 'dayjs';
import { Alert, Stack } from 'hephaestus/web';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../../../hooks/useLocalization';
import { usePermission } from '../../../../../../hooks/usePermission';
import type { MultiDepositEftProp } from './MultiDepositEft';

export type MultiDepositEftScheduleProp = Pick<
  MultiDepositEftProp,
  | 'options'
  | 'deposit'
  | 'updateContextWithFields'
  | 'focused'
  | 'focus'
  | 'showsFieldError'
> &
  Pick<MultiDepositEftProp['form'], 'schedule'>;

export const MultiDepositEftSchedule = ({
  options,
  deposit,
  updateContextWithFields,
  schedule,
  focused,
  focus,
  showsFieldError,
}: MultiDepositEftScheduleProp) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation('workflowCompletions');
  const canWriteTransferOboClients = usePermission(
    'write:transfer_obo_clients',
  );
  const canWriteTransferBasic = usePermission('write:transfer_basic');

  const visibleScheduleFields = resolveScheduleFieldsVisibility({
    deposit,
    starting: schedule.starting,
    options,
    canWriteTransferOboClients,
    canWriteTransferBasic,
  });

  const changeStarting = (starting: DepositStarting) => {
    schedule.setStarting(starting);
    if (starting === DepositStarting.FutureDate) {
      focus('scheduledDate');
    }
  };

  const changeScheduledDate = (date: Dayjs) => {
    focus('scheduledDate');
    return schedule.changeScheduledDate(date);
  };

  return (
    <>
      {visibleScheduleFields.schedule && (
        <SelectField
          fullWidth
          sx={{ mt: 2 }}
          testId={MULTI_PORTFOLIO_DEPOSIT_TEST_ID.SCHEDULE_SELECT}
          value={deposit?.schedule ?? schedule.defaultScheduleOption ?? ''}
          label={translateBackendString(options?.schedule?.label)}
          onChange={(event: { target: { value: Frequency } }) =>
            updateContextWithFields({ schedule: event.target.value })
          }
          onBlur={() => focus('schedule')}
          error={showsFieldError('schedule')}
        >
          {SCHEDULE_FREQUENCIES.map((frequency) => (
            <MenuItem
              key={frequency}
              value={frequency}
              data-testid={`${MULTI_PORTFOLIO_DEPOSIT_TEST_ID.SCHEDULE_SELECT}-${frequency}`}
            >
              {t(`schedule.${frequency}`)}
            </MenuItem>
          ))}
        </SelectField>
      )}
      {visibleScheduleFields.starting && (
        <RadioGroup
          testId={MULTI_PORTFOLIO_DEPOSIT_TEST_ID.STARTING}
          label={t('components:transferModal.starting')}
          sx={{ pb: 0, mt: 2 }}
          value={schedule.starting}
          onChange={(event: { target: { value: DepositStarting } }) =>
            changeStarting(event.target.value)
          }
        >
          <Stack direction='row'>
            {visibleScheduleFields.startingOptions.map((startingOption) => (
              <Radio
                key={startingOption}
                testId={`${MULTI_PORTFOLIO_DEPOSIT_TEST_ID.STARTING}-${startingOption}`}
                value={startingOption}
                label={t(
                  `components:transferModal.startingOptions.${startingOption}`,
                )}
              />
            ))}
          </Stack>
        </RadioGroup>
      )}
      {visibleScheduleFields.scheduledDate && (
        <DateField
          fullWidth
          sx={{ mt: 2 }}
          dataTestId={MULTI_PORTFOLIO_DEPOSIT_TEST_ID.SCHEDULED_DATE}
          onChange={changeScheduledDate}
          label={translateBackendString(options?.scheduledDate?.label)}
          minDate={resolveScheduledDateMinDate({
            options,
            transferDelayCheck: schedule.transferDelayCheck,
          })}
          shouldDisableDate={schedule.isScheduledDateDisabled}
          value={deposit?.scheduledDate}
          error={
            schedule.scheduledDateError && focused.includes('scheduledDate')
          }
          errorText={schedule.scheduledDateErrorMessage}
          autocomplete='off'
        />
      )}
      {visibleScheduleFields.transferDelayAlert && (
        <Alert
          severity='info'
          sx={{ mt: 2 }}
          data-testid={MULTI_PORTFOLIO_DEPOSIT_TEST_ID.TRANSFER_DELAY_ALERT}
        >
          {translateBackendString(options?.transferDelayDescription)}
        </Alert>
      )}
    </>
  );
};
